import { Box, Stack, Text, Icons, Badge } from "@sembark-travel/ui-base"
import { useEffect, useRef, useState } from "react"
import { IWhatsAppTemplateEditorValue } from "./types"

export type TWhatsAppRenderHtml = (
	value: IWhatsAppTemplateEditorValue
) => Promise<string>

const WHATSAPP_FONT =
	"SF Pro Text,SF Pro Icons,system,-apple-system,system-ui,BlinkMacSystemFont,Helvetica Neue,Helvetica,Lucida Grande,Kohinoor Devanagari,sans-serif"

export function WhatsAppPreviewPanel({
	value,
	renderHtml,
}: {
	value: IWhatsAppTemplateEditorValue
	renderHtml?: TWhatsAppRenderHtml
}) {
	const isAuthenticationTemplate = value.category === "AUTHENTICATION"
	const debounced = useDebounce(value, 350)
	const [html, setHtml] = useState<string>("")
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const inflight = useRef(0)

	useEffect(() => {
		if (!renderHtml) return
		const reqId = ++inflight.current
		setLoading(true)
		setError(null)
		renderHtml(debounced)
			.then((next) => {
				if (reqId !== inflight.current) return
				setHtml(next)
				setLoading(false)
			})
			.catch((e: unknown) => {
				if (reqId !== inflight.current) return
				setError(e instanceof Error ? e.message : "Preview failed")
				setLoading(false)
			})
	}, [debounced, renderHtml])

	return (
		<Box
			style={{ background: "#faf7eb", color: "black" }}
			paddingY="4"
			paddingX="4"
			minHeight="full"
		>
			<Stack gap="3">
				<Box
					display="flex"
					alignItems="center"
					justifyContent="between"
					paddingBottom="2"
					borderBottomWidth="1"
					borderColor={{ default: "emphasis" }}
					style={{ borderColor: "#e0dcc8" }}
				>
					<Text fontWeight="semibold" fontSize="sm" color="muted">
						Live Preview
					</Text>
					<Badge accent>WhatsApp</Badge>
				</Box>

				<Box maxWidth="lg" style={{ fontFamily: WHATSAPP_FONT }}>
					{!renderHtml ? (
						<Text color="muted" fontSize="sm">
							Preview is unavailable in this context.
						</Text>
					) : error ? (
						<Box
							paddingY="3"
							paddingX="3"
							rounded="md"
							style={{
								background: "#fef2f2",
								border: "1px solid #fecaca",
								color: "#991b1b",
							}}
						>
							<Text fontSize="sm">{error}</Text>
						</Box>
					) : html ? (
						<Box position="relative">
							<Box dangerouslySetInnerHTML={{ __html: html }} />
							{loading ? (
								<Box
									position="absolute"
									top="0"
									right="0"
									paddingX="2"
									paddingY="px"
									rounded="md"
									style={{ background: "rgba(255,255,255,0.7)" }}
								>
									<Text fontSize="xs" color="muted">
										Updating…
									</Text>
								</Box>
							) : null}
						</Box>
					) : loading ? (
						<Box
							paddingY="6"
							textAlign="center"
							style={{
								borderColor: "#d0d0d0",
								borderStyle: "dashed",
							}}
							borderWidth="1"
							rounded="lg"
						>
							<Text color="muted" fontSize="sm">
								Loading preview…
							</Text>
						</Box>
					) : (
						<Box
							paddingY="12"
							textAlign="center"
							borderWidth="1"
							rounded="lg"
							style={{ borderColor: "#d0d0d0", borderStyle: "dashed" }}
						>
							<Stack gap="2" alignItems="center">
								<Icons.Chat size="8" color="muted" />
								<Text color="muted" fontSize="sm">
									Start typing to see preview
								</Text>
							</Stack>
						</Box>
					)}
					<Box marginTop="1" display="flex" justifyContent="end">
						<Text fontSize="xs" color="muted">
							<Icons.Ok size="3" /> Delivered
						</Text>
					</Box>
				</Box>

				<Box
					bgColor="subtle"
					paddingX="3"
					paddingY="2"
					rounded="md"
					borderWidth="1"
					style={{
						backgroundColor: "#fff8dc",
						borderColor: "#f0e68c",
					}}
				>
					<Stack gap="1">
						<Text fontSize="xs" fontWeight="semibold" color="default">
							<Icons.LightBulb size="3" /> Preview Information
						</Text>
						<Text fontSize="xs" color="muted">
							{isAuthenticationTemplate
								? "This preview represents the restricted OTP-style WhatsApp authentication flow."
								: "Preview is rendered by the server. Variables show as chips; final rendering still depends on WhatsApp client behaviour."}
						</Text>
					</Stack>
				</Box>
			</Stack>
		</Box>
	)
}

function useDebounce<T>(value: T, delay = 350): T {
	const [debounced, setDebounced] = useState(value)
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay)
		return () => clearTimeout(t)
	}, [value, delay])
	return debounced
}
